import React, { Fragment, useCallback, useMemo, useState } from "react";
import * as S from "./CondensedFeatureGrid.styles";
import { ICondensedFeatureGridProps } from "./CondensedFeatureGrid.types";
import { useMedia } from "react-use";
import { BREAKPOINTS } from "layout";
import { CondensedFeatureGridCard } from "./CondensedFeatureGridCard/CondensedFeatureGridCard";

export type TCardVariant = "light" | "dark";

export const CondensedFeatureGrid = ({
  cards,
  isOverview,
  isInView,
}: Readonly<ICondensedFeatureGridProps>) => {
  const isMedium = useMedia(BREAKPOINTS.DESKTOP_AND_DOWN, false);
  const isSmall = useMedia(BREAKPOINTS.TABLET_AND_DOWN, false);

  const [activeCardId, setActiveCardId] = useState<string | null>(cards[0]?.id);

  const itemsPerRow = useMemo(() => {
    if (isSmall) {
      return 1;
    }

    return isMedium ? 2 : 4;
  }, [isSmall, isMedium]);

  const mappedData = useMemo(() => {
    const map = new Map();

    cards.forEach((card, index) => {
      const chunkIndex = Math.floor(index / itemsPerRow);

      if (!map.has(chunkIndex)) {
        map.set(chunkIndex, [card]);

        return;
      }

      map.set(chunkIndex, [...map.get(chunkIndex), card]);
    });

    return map;
  }, [cards, itemsPerRow]);

  const expandCard = useCallback(
    (id: string) => {
      setActiveCardId(activeCardId === id ? null : id);
    },
    [activeCardId],
  );

  return (
    <S.RowsWrapper>
      {Array.from(mappedData.values()).map((row, index) => (
        <S.Grid key={index}>
          {row.map((card) => (
            <Fragment key={card.id}>
              <CondensedFeatureGridCard
                card={card}
                isActive={activeCardId === card.id}
                isInView={isInView}
                itemsCountInRow={row.length}
                itemsPerRow={itemsPerRow}
                isOverview={isOverview}
                onSelectCard={() => expandCard(card.id)}
              />
            </Fragment>
          ))}
        </S.Grid>
      ))}
    </S.RowsWrapper>
  );
};
